import React, { FC, useEffect, useState } from 'react';
import { SortOrder } from 'antd/lib/table/interface';
import _ from 'lodash';
import { ParsedQuery } from 'query-string';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import PageSection from '../../HOC/PageSection/PageSection';
import { useReportsSetup, useSort } from '../../hooks';
import { useFilters } from '../../hooks/useFilters';
import { IGetSurveyOverviewResp, IPagingInfo, IReportsSetupItem } from '../../services/surveys';
import AddNewReportModal from '../AddNewReportModal/AddNewReportModal';
import DeleteReportModal from '../DeleteReportModal/DeleteReportModal';
import ImportModal from '../ImportModal/ImportModal';
import NoData from '../NoData/NoData';
import ReportResultsLegend from '../ReportResultsLegend/ReportResultsLegend';
import SearchBox from '../SearchBox/SearchBox';
import ReportsSetupTable from './ReportSetupTable/ReportsSetupTable';
import {
  ActionsCont,
  AddButton,
  Header,
  ImportButton,
  InfoDetail,
  InfoTitle,
  ReportsSetupCont,
  ReportsSetupTab,
  ReportsSetupTabsCont,
  SearchCont,
  SectionCont,
  TabTitle,
} from './ReportsSetup.styles';
import ReportsSetupTableHeader from './ReportsSetupTableHeader/ReportsSetupTableHeader';
import AddIcon from '@/assets/icons/addIcon.svg?react';
import ImportIcon from '@/assets/icons/download.svg?react';
import { isEffectLikeSurvey, surveyTypes } from '../../constans/forms';

const pagingInfoInitial = {
  pageNo: null,
  itemsPerPage: null,
  totalPageCount: null,
  totalItemsCount: null,
  memberCount: null,
  subordinateCount: null,
  superordinateCount: null,
  leaderCount: null,
  teamReportsCount: null,
  executiveReportsCount: null,
};

const ReportsSetup: FC<{ surveyOverview: IGetSurveyOverviewResp }> = ({ surveyOverview }) => {
  const [currentReport, setCurrentReport] = useState<IReportsSetupItem | undefined>(undefined);
  const [activeModal, setActiveModal] = useState(false);
  const [activeDeleteModal, setActiveDeleteModal] = useState(false);
  const [activeImportModal, setActiveImportModal] = useState(false);
  const { t } = useTranslation();
  const { surveyId } = useParams();
  const { deleteReport, downloadReportsSetup } = useReportsSetup();
  const [reportsGroup, setReportsGroup] = useState<IReportsSetupItem[]>([]);
  const [pagingInfo, setPagingInfo] = useState<IPagingInfo>(pagingInfoInitial);
  const [type, setType] = useState<'team' | 'executive' | 'all'>('all');
  const [reportsCount, setReportsCount] = useState({ all: 0, team: 0, executive: 0 });
  const { applyFilters, currentFilters } = useFilters();
  const isEffectSurvey = isEffectLikeSurvey(surveyOverview.type);
  const isGoodTeamSurvey = surveyOverview.type === surveyTypes.GOOD_TEAM;

  const { setSortQuery } = useSort();
  const { perPage, pageNo, search, sortOrder } = currentFilters;

  const updateList = async (overrideFilters: ParsedQuery = {}) => {
    const results = await downloadReportsSetup(
      surveyId || '0',
      type === 'all' ? undefined : type,
      overrideFilters
    );

    if (type === 'all') {
      const allReportsCount = results.pagingInfo.totalItemsCount || 0;
      const teamReportsCount = results.pagingInfo.teamReportsCount || 0;
      const executiveReportsCount = results.pagingInfo.executiveReportsCount || 0;

      setReportsCount({
        all: allReportsCount,
        team: teamReportsCount,
        executive: executiveReportsCount,
      });
    } else {
      setReportsCount(prev => ({
        ...prev,
        [type]: results.pagingInfo.totalItemsCount || 0,
      }));
    }

    setReportsGroup(results.items);
    setPagingInfo(results.pagingInfo);
  };

  const handleSearch = (filters: Record<string, string | null | undefined>) => {
    applyFilters(filters);
  };

  const handleSort = (sorter: { field: string; order: SortOrder }) => {
    applyFilters({ sortOrder: setSortQuery(sorter) });
  };

  const changeTab = (tab: 'team' | 'executive' | 'all') => {
    applyFilters({
      pageNo: null,
      reports: null,
    });
    setType(tab);
  };

  const handleDeleteModal = (data?: IReportsSetupItem) => {
    setCurrentReport(data);
    setActiveDeleteModal(activeModal => !activeModal);
  };

  const handleDeleteClose = () => {
    handleDeleteModal(undefined);
  };

  const handleDeleteConfirm = async () => {
    if (!currentReport?.id) return;

    const pageNumber = reportsGroup?.length !== 1 ? pageNo : '1';

    try {
      await deleteReport(currentReport.id);
      await updateList({ pageNo: pageNumber });

      if (pageNumber !== pageNo) {
        applyFilters({ pageNo: pageNumber as string | null | undefined });
      }
    } finally {
      handleDeleteModal(undefined);
    }
  };

  const handleModal = (status: boolean, data?: IReportsSetupItem) => {
    const editData = data || undefined;
    if (editData) {
      setCurrentReport(editData);
      return setActiveModal(status);
    }
    setCurrentReport(editData);
    return setActiveModal(status);
  };

  useEffect(() => {
    (async () => {
      await updateList();
    })();
  }, [surveyId, pageNo, perPage, search, sortOrder, type]);

  return (
    <ReportsSetupCont id="ReportsSetupCont">
      <Header id="Header">
        {isEffectSurvey ? (
          <InfoTitle>
            {t('viewSurvey.topBar.reportsSetup')}:<InfoDetail>{reportsCount.all}</InfoDetail>
          </InfoTitle>
        ) : (
          <>
            <InfoTitle>
              {t('viewSurvey.reportsSetup.allReports')}:<InfoDetail>{reportsCount.all}</InfoDetail>
            </InfoTitle>
            <InfoTitle>
              {t('viewSurvey.reportsSetup.teamReports')}:
              <InfoDetail>{reportsCount.team}</InfoDetail>
            </InfoTitle>
            <InfoTitle>
              {t('viewSurvey.reportsSetup.executiveReports')}:
              <InfoDetail>{reportsCount.executive}</InfoDetail>
            </InfoTitle>
          </>
        )}
      </Header>
      <AddNewReportModal
        active={activeModal}
        editData={currentReport}
        setActive={() => handleModal(false)}
        refreshList={updateList}
        isEffectSurvey={isEffectSurvey}
      />
      <DeleteReportModal
        active={activeDeleteModal}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
      <ImportModal
        active={activeImportModal}
        kind="reportingGroups"
        surveyId={surveyId || '1'}
        onClose={() => setActiveImportModal(false)}
        onImported={() => updateList()}
        isEffectSurvey={isEffectSurvey}
      />
      <SearchCont id="SearchCont">
        <SearchBox
          filterName="search"
          searchPhrase={handleSearch}
          placeholder={t('viewSurvey.reportsSetup.search')}
          isButtonAvailable={false}
          isIcon
        />
        <ActionsCont>
          <ImportButton id="ImportButton" onClick={() => setActiveImportModal(true)}>
            <ImportIcon id="ImportIcon" />
            {t('imports.button.reportingGroups')}
          </ImportButton>
          <AddButton id="AddButton" onClick={() => handleModal(true)}>
            <AddIcon id="AddIcon" />
            {t('viewSurvey.reportsSetup.addReport')}
          </AddButton>
        </ActionsCont>
      </SearchCont>
      <ReportsSetupTabsCont id="ReportsSetupTabsCont">
        {isEffectSurvey ? null : (
          <>
            <ReportsSetupTab
              id="ReportsSetupTab"
              data-testid="reportsSetupTab"
              active={type === 'all'}
              onClick={() => changeTab('all')}
            >
              <TabTitle id="TabTitle">{t('viewSurvey.reportsSetup.allReports')}</TabTitle>
            </ReportsSetupTab>
            <ReportsSetupTab
              id="ReportsSetupTeamTab"
              data-testid="reportsSetupTeamTab"
              active={type === 'team'}
              onClick={() => changeTab('team')}
            >
              <TabTitle id="TabTitle">{t('viewSurvey.reportsSetup.teamReports')}</TabTitle>
            </ReportsSetupTab>
            <ReportsSetupTab
              id="ReportsSetupExecutiveTab"
              data-testid="reportsSetupExecutiveTab"
              active={type === 'executive'}
              onClick={() => changeTab('executive')}
            >
              <TabTitle id="TabTitle">{t('viewSurvey.reportsSetup.executiveReports')}</TabTitle>
            </ReportsSetupTab>
          </>
        )}
      </ReportsSetupTabsCont>
      <PageSection>
        <SectionCont id="SectionCont">
          <ReportsSetupTableHeader
            appliedSort={sortOrder}
            sort={handleSort}
            isEffectSurvey={isEffectSurvey}
          />
          <ReportResultsLegend
            description={
              isGoodTeamSurvey
                ? t('viewSurvey.reportsSetup.goodTeamDescriptionText')
                : isEffectSurvey
                  ? t('viewSurvey.reportsSetup.effectDescriptionText')
                  : t('viewSurvey.reportsSetup.descriptionText')
            }
            variant="simple"
            hideExpand={isEffectSurvey}
          />
          {_.isEmpty(reportsGroup) ? (
            <NoData message={t('viewSurvey.reportsSetup.noReportsInfo')} />
          ) : (
            <ReportsSetupTable
              deleteItem={(item: IReportsSetupItem) => handleDeleteModal(item)}
              editItem={(item: IReportsSetupItem) => handleModal(true, item)}
              updateFullTable={updateList}
              reportsGroup={reportsGroup}
              pagingInfo={pagingInfo}
              applyFilters={applyFilters}
              isEffectSurvey={isEffectSurvey}
            />
          )}
        </SectionCont>
      </PageSection>
    </ReportsSetupCont>
  );
};

export default ReportsSetup;
